import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { TrendingDown, Users, DollarSign } from 'lucide-react';
import { useIsDark } from '../../useIsDark';

interface RoleCost {
  role: string;
  salary: number;
  count: number;
  total: number;
}

const humanRoles: RoleCost[] = [
  { role: 'VP of Sales', salary: 180000, count: 1, total: 180000 },
  { role: 'Sales Reps (×2)', salary: 65000, count: 2, total: 130000 },
  { role: 'Fundraising', salary: 150000, count: 1, total: 150000 },
  { role: 'Recruiter', salary: 80000, count: 1, total: 80000 },
  { role: 'Investor Relations', salary: 120000, count: 1, total: 120000 },
  { role: 'CRM / Ops', salary: 75000, count: 1, total: 75000 },
  { role: 'Data Analyst', salary: 90000, count: 1, total: 90000 },
  { role: 'Marketing', salary: 70000, count: 1, total: 70000 },
  { role: 'Customer Success', salary: 75000, count: 1, total: 75000 },
  { role: 'Bookkeeper', salary: 60000, count: 1, total: 60000 },
  { role: 'HR', salary: 55000, count: 1, total: 55000 },
];

const HUMAN_TOTAL = 1185000;

export const CostComparisonChart = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const isDark = useIsDark();

  // Canvas chrome follows the class-based theme; bar gradients stay identical.
  const C = isDark
    ? {
        tooltipBg: 'rgba(15, 23, 42, 0.95)',
        tooltipBorder: '#334155',
        tooltipText: '#f8fafc',
        tooltipMuted: '#94a3b8',
        axis: '#94a3b8',
        axisStrong: '#e2e8f0',
        grid: '#1e293b',
      }
    : {
        tooltipBg: '#ffffff',
        tooltipBorder: '#e2e8f0',
        tooltipText: '#0f172a',
        tooltipMuted: '#64748b',
        axis: '#64748b',
        axisStrong: '#334155',
        grid: '#e2e8f0',
      };

  const chartOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: C.tooltipBg,
      borderColor: C.tooltipBorder,
      textStyle: { color: C.tooltipText },
      formatter: (params: any[]) => {
        const p = params[0];
        return `<div style="font-weight:600">${p.name}</div>
                <div style="color:${C.tooltipMuted}">$${p.value.toLocaleString()}/year</div>`;
      },
    },
    grid: {
      left: 100,
      right: 40,
      top: 20,
      bottom: 20,
    },
    xAxis: {
      type: 'value',
      max: 200000,
      axisLabel: {
        formatter: (val: number) => `$${(val / 1000)}K`,
        color: C.axis,
        fontSize: 11,
      },
      splitLine: { lineStyle: { color: C.grid } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'category',
      data: humanRoles.map((r) => r.role),
      axisLabel: {
        color: C.axisStrong,
        fontSize: 11,
        fontWeight: 500,
      },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        type: 'bar',
        data: humanRoles.map((r, i) => ({
          value: r.salary,
          itemStyle: {
            color: i % 2 === 0
              ? new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                  { offset: 0, color: '#0AB3BF' },
                  { offset: 1, color: '#4DD4E0' },
                ])
              : new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                  { offset: 0, color: '#087B8B' },
                  { offset: 1, color: '#0AB3BF' },
                ]),
            borderRadius: [0, 6, 6, 0],
          },
        })),
        barWidth: 20,
        animationDuration: 1500,
        animationDelay: (idx: number) => idx * 80,
        animationEasing: 'elasticOut',
      },
    ],
  };

  return (
    <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="text-center mb-12"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-500/10 dark:text-teal-400 rounded-full text-sm font-medium border dark:border-teal-500/20 mb-6">
          <DollarSign className="w-4 h-4" />
          The Economics: Fully Staffed vs. Raisy
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
          A Full AI Workforce for Less Than{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-cyan-500 dark:from-teal-400 dark:to-cyan-400">
            One Junior Salary
          </span>
        </h2>
        <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
          What it costs to staff these functions full-time vs. what you pay with Raisy.
          Same coverage. A fraction of the cost — and your people keep the final call.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Chart */}
        <motion.div
          className="lg:col-span-7 bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-xl p-6 backdrop-blur-sm"
          initial={{ opacity: 0, x: -40 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Fully Staffed Salary Stack</h3>
          </div>
          <ReactECharts
            option={chartOption}
            style={{ height: '420px', width: '100%' }}
            opts={{ renderer: 'canvas' }}
          />
        </motion.div>

        {/* Summary Cards */}
        <motion.div
          className="lg:col-span-5 space-y-4"
          initial={{ opacity: 0, x: 40 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {/* Fully staffed cost */}
          <div className="bg-white dark:bg-slate-800/60 border border-red-200 dark:border-red-500/20 rounded-xl p-6 backdrop-blur-sm">
            <div className="text-sm text-red-600 dark:text-red-400 font-medium mb-1">Fully Staffed Team</div>
            <div className="text-4xl font-bold text-slate-900 dark:text-white">
              ${(HUMAN_TOTAL / 1000000).toFixed(3)}M
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">Annual cost (11 FTEs + overhead)</div>
          </div>

          {/* Raisy Cost */}
          <div className="bg-white dark:bg-slate-800/60 border border-teal-200 dark:border-teal-500/30 rounded-xl p-6 backdrop-blur-sm">
            <div className="text-sm text-teal-600 dark:text-teal-400 font-medium mb-1">Raisy AI Workforce</div>
            <div className="text-4xl font-bold text-slate-900 dark:text-white">
              ~1 junior salary
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">Your processes, run by hundreds of bots</div>
          </div>

          {/* Savings */}
          <div className="bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border border-teal-200 dark:border-teal-500/30 rounded-xl p-6 backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              <div className="text-sm text-teal-600 dark:text-teal-400 font-medium">What You Keep</div>
            </div>
            <div className="text-4xl font-bold text-slate-900 dark:text-white">
              Most of your runway
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
              The cost of a full team, freed up for product and growth — instead of headcount.
            </p>
          </div>

          {/* Coverage */}
          <div className="bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/30 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600 dark:text-slate-300">Processes built for you</div>
              <div className="text-lg font-bold text-cyan-600 dark:text-cyan-400">Every function</div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="text-sm text-slate-600 dark:text-slate-300">Specialized bots</div>
              <div className="text-lg font-bold text-teal-600 dark:text-teal-400">One per step</div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="text-sm text-slate-600 dark:text-slate-300">Human oversight</div>
              <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">Always on</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
